import { useId } from "react";
import { CodiceFiscale } from "codice-fiscale-js";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import AddressAutocomplete from "./AddressAutocomplete";
import DateInput from "./DateInput";
import { searchComuni } from "../lib/comuni";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";

const LBL = "text-[11px] uppercase tracking-wider text-slate-600 font-mono";
const INP = "mt-1.5 h-9 bg-slate-900/60 border-slate-700 text-slate-100";
const TAB = "data-[state=active]:bg-amber-500 data-[state=active]:text-[#0a0d14] text-slate-600 font-semibold";

function F({ label, k, form, set, testId, mono, upper, span }) {
  return (
    <div className={span ? "sm:col-span-2" : ""}>
      <Label className={LBL}>{label}</Label>
      <Input value={form[k] || ""} onChange={(e) => set(k, upper ? e.target.value.toUpperCase() : e.target.value)} data-testid={testId} className={`${INP} ${mono ? "font-mono" : ""}`} />
    </div>
  );
}

// Comune con suggerimenti comuni italiani: alla scelta compila provincia e CAP.
function ComuneF({ label, value, onChange, onPick, testId }) {
  const id = useId();
  const opts = value && value.length >= 2 ? searchComuni(value, 15) : [];
  return (
    <div>
      <Label className={LBL}>{label}</Label>
      <Input list={id} value={value || ""} data-testid={testId} className={INP}
        onChange={(e) => { const v = e.target.value; onChange(v); const m = v.length >= 2 ? searchComuni(v, 15).find((c) => c.nome.toLowerCase() === v.toLowerCase()) : null; if (m) onPick(m); }} />
      <datalist id={id}>{opts.map((c) => <option key={`${c.nome}-${c.provincia}`} value={c.nome}>{c.provincia}</option>)}</datalist>
    </div>
  );
}

// Scheda soggetto IVA (dialog pratica): tab Dati Aziendali + tab Amministratore / Rappresentante.
export default function SoggettoIvaTabs({ form, set, setForm, testId }) {
  const patch = (o) => setForm((f) => ({ ...f, ...o }));
  const generaCF = () => {
    const [y, m, d] = String(form.rappr_data_nascita || "").slice(0, 10).split("-").map(Number);
    if (!(form.rappr_cognome && form.rappr_nome && form.rappr_sesso && y && form.rappr_luogo_nascita)) { toast.error("Per generare il CF compila cognome, nome, sesso, data e comune di nascita"); return; }
    try {
      const cf = new CodiceFiscale({ name: form.rappr_nome, surname: form.rappr_cognome, gender: /^f/i.test(form.rappr_sesso) ? "F" : "M", day: d, month: m, year: y, birthplace: form.rappr_luogo_nascita, birthplaceProvincia: form.rappr_provincia_nascita || undefined }).toString();
      set("rappr_codice_fiscale", cf); toast.success("Codice fiscale generato");
    } catch { toast.error("Comune di nascita non riconosciuto"); }
  };
  return (
    <Tabs defaultValue="azienda" className="w-full" data-testid={`${testId}-azienda-tabs`}>
      <TabsList className="bg-slate-100 border border-slate-300 flex-wrap h-auto">
        <TabsTrigger value="azienda" className={TAB} data-testid={`${testId}-tab-azienda`}>Dati Aziendali</TabsTrigger>
        <TabsTrigger value="rappr" className={TAB} data-testid={`${testId}-tab-rappr`}>Amministratore / Rappresentante</TabsTrigger>
      </TabsList>
      <TabsContent value="azienda" className="mt-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label className={LBL}>Ragione Sociale</Label>
            <Input value={form.cognome || ""} onChange={(e) => patch({ cognome: e.target.value, nome: "" })} data-testid={`${testId}-rs`} className={`${INP} uppercase`} />
          </div>
          <F label="Partita IVA" k="partita_iva" form={form} set={set} testId={`${testId}-piva`} mono upper />
          <F label="Codice Fiscale azienda" k="codice_fiscale" form={form} set={set} testId={`${testId}-cf-azienda`} mono upper />
          <F label="Codice Destinatario SDI" k="codice_destinatario" form={form} set={set} testId={`${testId}-sdi`} mono upper />
          <F label="PEC" k="pec" form={form} set={set} testId={`${testId}-az-pec`} />
          <div className="sm:col-span-2">
            <Label className={LBL}>Indirizzo sede legale</Label>
            <div className="mt-1.5"><AddressAutocomplete value={form.indirizzo} onChange={(v) => set("indirizzo", v)} onPick={(p) => patch({ indirizzo: p.indirizzo, comune: p.comune, cap: p.cap, provincia: p.provincia })} city={form.comune} prov={form.provincia} testId={`${testId}-az-indirizzo`} /></div>
          </div>
          <F label="N° Civ." k="numero_civico" form={form} set={set} testId={`${testId}-az-civico`} />
          <ComuneF label="Comune" value={form.comune} onChange={(v) => set("comune", v)} onPick={(c) => patch({ provincia: c.provincia || "", cap: c.cap || form.cap })} testId={`${testId}-az-comune`} />
          <F label="Provincia" k="provincia" form={form} set={set} testId={`${testId}-az-provincia`} upper />
          <F label="CAP" k="cap" form={form} set={set} testId={`${testId}-az-cap`} mono />
          <F label="Tel. Ufficio" k="tel_ufficio" form={form} set={set} testId={`${testId}-az-teluff`} />
          <F label="Email azienda" k="email" form={form} set={set} testId={`${testId}-az-email`} />
        </div>
      </TabsContent>
      <TabsContent value="rappr" className="mt-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <F label="Cognome" k="rappr_cognome" form={form} set={set} testId={`${testId}-rappr-cognome`} />
          <F label="Nome" k="rappr_nome" form={form} set={set} testId={`${testId}-rappr-nome`} />
          <div>
            <Label className={LBL}>Sesso</Label>
            <div className="mt-1.5 flex gap-2">
              {["Maschio", "Femmina"].map((s) => (
                <button key={s} type="button" onClick={() => set("rappr_sesso", s)} data-testid={`${testId}-rappr-sesso-${s}`}
                  className={`h-9 px-3 rounded-md border text-sm transition-colors ${form.rappr_sesso === s ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}>{s}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className={LBL}>Data di nascita</Label>
            <div className="mt-1.5"><DateInput value={form.rappr_data_nascita || ""} onChange={(v) => set("rappr_data_nascita", v)} testId={`${testId}-rappr-nascita`} className="h-9 bg-slate-900/60 border-slate-700 text-slate-100" /></div>
          </div>
          <ComuneF label="Comune di nascita" value={form.rappr_luogo_nascita} onChange={(v) => set("rappr_luogo_nascita", v)} onPick={(c) => set("rappr_provincia_nascita", c.provincia || "")} testId={`${testId}-rappr-comune-nascita`} />
          <F label="Provincia di nascita" k="rappr_provincia_nascita" form={form} set={set} testId={`${testId}-rappr-prov-nascita`} upper />
          <div className="sm:col-span-2">
            <Label className={LBL}>Codice Fiscale personale</Label>
            <div className="mt-1.5 flex gap-2">
              <Input value={form.rappr_codice_fiscale || ""} onChange={(e) => set("rappr_codice_fiscale", e.target.value.toUpperCase())} data-testid={`${testId}-rappr-cf`} className="h-9 bg-slate-900/60 border-slate-700 font-mono flex-1" />
              <Button type="button" onClick={generaCF} data-testid={`${testId}-rappr-genera-cf`} className="h-9 bg-cyan-600 hover:bg-cyan-700 text-white shrink-0"><Sparkles className="h-4 w-4 mr-1" /> Genera CF</Button>
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label className={LBL}>Indirizzo</Label>
            <div className="mt-1.5"><AddressAutocomplete value={form.rappr_indirizzo} onChange={(v) => set("rappr_indirizzo", v)} onPick={(p) => patch({ rappr_indirizzo: p.indirizzo, rappr_comune: p.comune, rappr_cap: p.cap, rappr_provincia: p.provincia })} city={form.rappr_comune} prov={form.rappr_provincia} testId={`${testId}-rappr-indirizzo`} /></div>
          </div>
          <F label="N° Civ." k="rappr_numero_civico" form={form} set={set} testId={`${testId}-rappr-civico`} />
          <ComuneF label="Comune" value={form.rappr_comune} onChange={(v) => set("rappr_comune", v)} onPick={(c) => patch({ rappr_provincia: c.provincia || "", rappr_cap: c.cap || form.rappr_cap })} testId={`${testId}-rappr-comune`} />
          <F label="Provincia" k="rappr_provincia" form={form} set={set} testId={`${testId}-rappr-provincia`} upper />
          <F label="CAP" k="rappr_cap" form={form} set={set} testId={`${testId}-rappr-cap`} mono />
          <F label="Cellulare" k="rappr_cellulare" form={form} set={set} testId={`${testId}-rappr-cell`} />
          <F label="Email personale" k="rappr_email" form={form} set={set} testId={`${testId}-rappr-email`} />
        </div>
      </TabsContent>
    </Tabs>
  );
}
